/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Box,
  debounce,
  Paper,
  PaperProps,
  TextField,
} from '@mui/material';
import { GridRenderCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid';
import { TaskAccountType } from 'api/accounts';
import { useUpdateDraftWorkflowRequest } from 'components/Requests/hooks/useUpdateDraftWorkflowRequest';
import { WorkflowRequestsWorkflowRequestFragment } from 'gql/graphql';
import { useAccountSearchByKeyword } from 'hooks/accounts-hooks';
import { useEffect, useMemo, useState } from 'react';
import { TooltipStyled } from 'ui/TooltipStyled/TooltipStyled';
import SearchIcon from '@mui/icons-material/Search';

type AccountCellProps = {
  params: GridRenderCellParams<
    WorkflowRequestsWorkflowRequestFragment,
    any,
    any,
    GridTreeNodeWithRender
  >;
};

export const AccountCell = ({ params }: AccountCellProps) => {
  const {
    assignedAccount,
    assignedContacts,
    id: workflowRequestId,
  } = params.row;
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedAccount, setSelectedAccount] =
    useState<TaskAccountType | null>(null);
  const { data: accountList } = useAccountSearchByKeyword(searchKeyword);
  const updateWorkflowRequest = useUpdateDraftWorkflowRequest();

  useEffect(() => {
    if (assignedAccount) {
      handleSearchField(assignedAccount.name);
      setSelectedAccount({
        label: assignedAccount.name,
        uuid: (assignedAccount.id || 0) as number,
      });
    } else {
      setSelectedAccount(null);
    }
  }, [assignedAccount]);

  const handleSearchField = useMemo(
    () =>
      debounce((searchText: string) => {
        setSearchKeyword(searchText);
      }, 500),
    [],
  );

  const handleOptionSelected = (newOption: TaskAccountType | null) => {
    setSelectedAccount(newOption);

    const updatedRow = {
      ...params.row,
      assignedAccount: {
        id: newOption?.uuid,
        legacyId: (newOption?.value || '') as string,
        name: newOption?.label,
      },
    };

    params.api.updateRows([updatedRow]);

    updateWorkflowRequest(workflowRequestId, {
      assignedAccountId: newOption?.uuid,
    });
  };

  const toolTipText =
    assignedContacts && assignedContacts?.length > 0
      ? 'The "Assigned To" field must be blank in order to change the "Account"'
      : '';

  return (
    <Box
      display="flex"
      gap="5px"
      flexDirection="column"
      alignItems="start"
      width="100%"
      onClick={(event) => event.stopPropagation()}
    >
      <TooltipStyled title={toolTipText}>
        <Autocomplete
          key={`account-${workflowRequestId}`}
          popupIcon={null}
          // eslint-disable-next-line react/no-unstable-nested-components
          PaperComponent={(props: PaperProps) => {
            if (!accountList?.length) return null;
            return <Paper {...props} />;
          }}
          disabled={toolTipText.length > 0}
          options={accountList || []}
          getOptionLabel={(option) => option.label}
          noOptionsText=""
          isOptionEqualToValue={(option, value) => option.label === value.label}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              InputProps={{
                ...inputParams.InputProps,
                startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
              }}
              placeholder="Search"
              variant="standard"
              fullWidth
              onChange={(e) => handleSearchField(e.target.value)}
            />
          )}
          onChange={(event, newOption, reason) => {
            if (reason === 'clear') {
              event.preventDefault();
              event.stopPropagation();
            } else {
              handleOptionSelected(newOption || null);
            }
          }}
          filterOptions={(x) => x}
          value={selectedAccount}
          blurOnSelect
          fullWidth
        />
      </TooltipStyled>
    </Box>
  );
};
